import React, { useEffect, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    TextField,
    Paper,
    IconButton
} from '@mui/material'
import { Icon } from '@iconify/react'
import axios from 'axios'
import apiUrl from '../../configs/constant'
import { useToast } from '../../components/ToastProvider'

interface ContactNote {
    id: number
    tgl: string
    keterangan: string
}

interface Props {
    open: boolean
    contact?: {
        sdcust_id: number
    }
    onClose: () => void
}

export default function DialogCatatan({ open, contact, onClose }: Props) {
    const { showToast } = useToast()

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    const token = userData?.token

    const [notes, setNotes] = useState<ContactNote[]>([])
    const [draft, setDraft] = useState('')
    const [editingId, setEditingId] = useState<number | null>(null)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)

    const resetForm = () => {
        setDraft('')
        setEditingId(null)
    }

    const loadNotes = async () => {
        if (!contact?.sdcust_id) return

        try {
            const res = await axios.get(
                `${apiUrl.apiUrl}/omni/notes/get-notes/${contact.sdcust_id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )

            if (res.data.success) {
                setNotes(res.data.data)
            }
        } catch {
            showToast('Gagal mengambil catatan', 'error')
        }
    }

    useEffect(() => {
        if (open) {
            loadNotes()
            resetForm()
        }
    }, [open, contact?.sdcust_id])

    const saveNote = async () => {
        if (!draft.trim() || !contact) return

        setLoading(true)

        try {
            const payload = {
                sdcust_id: contact.sdcust_id,
                tgl: new Date().toISOString().slice(0, 10),
                keterangan: draft
            }

            let res

            if (editingId) {
                res = await axios.put(
                    `${apiUrl.apiUrl}/omni/notes/edit/${editingId}`,
                    payload,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            } else {
                res = await axios.post(
                    `${apiUrl.apiUrl}/omni/notes/add`,
                    payload,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            }

            if (res.data.success) {
                showToast(
                    editingId ? 'Catatan diperbarui' : 'Catatan ditambahkan',
                    'success'
                )
                resetForm()
                loadNotes()
            }
        } catch {
            showToast('Gagal menyimpan catatan', 'error')
        } finally {
            setLoading(false)
        }
    }

    const startEdit = (note: ContactNote) => {
        setEditingId(note.id)
        setDraft(note.keterangan)
    }

    const deleteNote = async () => {
        if (!deleteId) return

        try {
            const res = await axios.delete(
                `${apiUrl.apiUrl}/omni/notes/delete/${deleteId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )

            if (res.data.success) {
                showToast('Catatan dihapus', 'success')
                loadNotes()
            }
        } catch {
            showToast('Gagal menghapus catatan', 'error')
        } finally {
            setDeleteId(null)
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Catatan</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.5} mb={2}>
                        {notes.length === 0 && (
                            <Typography color="text.secondary">
                                Belum ada catatan
                            </Typography>
                        )}

                        {notes.map(note => (
                            <Paper
                                key={note.id}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    <Typography variant="body2">
                                        {note.keterangan}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {note.tgl}
                                    </Typography>
                                </div>

                                <Stack direction="row" spacing={0.5}>
                                    <IconButton
                                        size="small"
                                        onClick={() => startEdit(note)}
                                    >
                                        <Icon icon="mdi:pencil" />
                                    </IconButton>

                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteId(note.id)}
                                    >
                                        <Icon icon="mdi:delete" />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>


                    <Typography color="text.primary">
                        {editingId ? 'Edit Catatan' : 'Tambah Catatan'}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Tulis catatan..."
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        disabled={loading}
                    />
                </DialogContent>

                <DialogActions>
                    {editingId && (
                        <Button
                            onClick={resetForm}
                            disabled={loading}
                            color="inherit"
                        >
                            Batal Edit
                        </Button>
                    )}

                    <Button onClick={onClose} disabled={loading}>
                        Tutup
                    </Button>

                    <Button
                        variant="contained"
                        onClick={saveNote}
                        disabled={loading || !draft.trim()}
                    >
                        {editingId ? 'Edit' : 'Tambah'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===================== DELETE CONFIRM ===================== */}

            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>Hapus Catatan?</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteId(null)}>Batal</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={deleteNote}
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
